"use client";

import { Button } from "@ui/components/core/Button";
import { clsx } from "clsx";
import { XIcon } from "lucide-react";
import { useTranslations } from "next-intl";

interface ConvAITermsProps {
  onAgree: () => void;
  onClose: () => void;
  /** Reduces margins and list spacing to fit in tighter vertical spaces (e.g. small viewports). */
  compact?: boolean;
  /**
   * Optional className applied to the wrapper around the intro paragraph + bullets. Lets consumers pad
   * the textual content independently from the actions row (e.g. content at 24px from card edges while
   * buttons sit at 12px).
   */
  contentClassName?: string;
}

interface ConvAITermsModalProps extends ConvAITermsProps {
  /**
   * `"default"` (default) — fixed backdrop with a wide centered card.
   * `"portrait"` — fixed backdrop with a narrow card whose height exceeds its width,
   * suitable for widget contexts like the Speech Engine demo.
   */
  variant?: "default" | "portrait";
}

export function ConvAITerms({ onAgree, onClose, compact = false, contentClassName }: ConvAITermsProps) {
  const t = useTranslations("ClientKeys.hero_video_agent");

  return (
    <>
      <div className={contentClassName}>
        <p className={clsx("tw-type-base tw-text-pretty tw-pr-8 tw-text-black", compact ? "tw-mb-3" : "tw-mb-6")}>
          {t("terms.intro")}
        </p>

        <ul
          role='list'
          className={clsx(
            "tw-text-xs/xs tw-text-gray-500",
            compact ? "tw-mb-3 tw-space-y-1.5" : "tw-mb-6 tw-space-y-3",
          )}
        >
          <li className='tw-gap-2.5 tw-flex tw-items-start'>
            <span className='tw-bg-gray-400 tw-block tw-size-1 tw-flex-none tw-rounded-full tw-mt-[0.53125rem]' />
            <span>{t("terms.age_requirement")}</span>
          </li>
          <li className='tw-gap-2.5 tw-flex tw-items-start'>
            <span className='tw-bg-gray-400 tw-block tw-size-1 tw-flex-none tw-rounded-full tw-mt-[0.53125rem]' />
            <span>{t("terms.ai_acknowledgement")}</span>
          </li>
          <li className='tw-gap-2.5 tw-flex tw-items-start'>
            <span className='tw-bg-gray-400 tw-block tw-size-1 tw-flex-none tw-rounded-full tw-mt-[0.53125rem]' />
            <span>{t("terms.recording_consent")}</span>
          </li>
          <li className='tw-gap-2.5 tw-flex tw-items-start'>
            <span className='tw-bg-gray-400 tw-block tw-size-1 tw-flex-none tw-rounded-full tw-mt-[0.53125rem]' />
            <span>
              {t.rich("terms.privacy_policy", {
                link: chunks => (
                  <a
                    href='https://elevenlabs.io/privacy-policy'
                    target='_blank'
                    rel='noopener noreferrer'
                    className='tw-text-black tw-underline tw-underline-offset-2 hover:tw-no-underline'
                  >
                    {chunks}
                  </a>
                ),
              })}
            </span>
          </li>
          <li className='tw-gap-2.5 tw-flex tw-items-start'>
            <span className='tw-bg-gray-400 tw-block tw-size-1 tw-flex-none tw-rounded-full tw-mt-[0.53125rem]' />
            <span>{t("terms.data_sharing_consent")}</span>
          </li>
        </ul>
      </div>

      <div className={clsx("tw-grid tw-grid-cols-2 tw-gap-2", compact ? "tw-pb-0" : "tw-pb-1")}>
        <Button onClick={onClose} variant='elevated' size={compact ? "md" : "lg"} className='tw-w-full'>
          {t("terms.cancel_button")}
        </Button>
        <Button onClick={onAgree} variant='primary' size={compact ? "md" : "lg"} className='tw-w-full'>
          {t("terms.agree_button")}
        </Button>
      </div>
    </>
  );
}

export function ConvAITermsModal({ onAgree, onClose, variant = "default" }: ConvAITermsModalProps) {
  const t = useTranslations("ClientKeys.hero_video_agent");

  if (variant === "portrait") {
    return (
      <div className='tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-black/60 tw-backdrop-blur-sm'>
        <div
          className='tw-relative tw-flex tw-w-[320px] tw-flex-col tw-rounded-3xl tw-bg-white tw-p-5'
          style={{ boxShadow: "0px 8px 32px rgba(0, 0, 0, 0.2)" }}
        >
          <button
            className='tw-absolute tw-right-3 tw-top-3 tw-flex tw-size-8 tw-items-center tw-justify-center tw-rounded-full tw-text-gray-400 tw-outline-none tw-transition-colors hover:tw-bg-gray-100 hover:tw-text-gray-700 focus-visible:tw-outline-focus'
            onClick={onClose}
            aria-label={t("terms.close")}
          >
            <XIcon size={16} />
          </button>
          <ConvAITerms onAgree={onAgree} onClose={onClose} compact />
        </div>
      </div>
    );
  }

  return (
    <div className='bg-black/60 fixed inset-0 z-50 flex items-center justify-center px-16 backdrop-blur-sm'>
      <div
        className='relative flex w-full max-w-[460px] flex-col rounded-[24px] bg-default p-28'
        style={{
          boxShadow: "0px 8px 32px rgba(0, 0, 0, 0.2)",
        }}
      >
        <button
          className='absolute right-16 top-16 flex h-32 w-32 items-center justify-center rounded-full text-light transition-colors hover:bg-overlay-dark hover:text-default'
          onClick={onClose}
          aria-label={t("terms.close")}
        >
          <XIcon size={18} />
        </button>

        <ConvAITerms onAgree={onAgree} onClose={onClose} />
      </div>
    </div>
  );
}
